import { useState } from 'react';
import { X, Send, XCircle, CheckCircle } from 'lucide-react';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { timeAgo } from '@dashboard/shared/timeAgo';
import type { Invoice, InvoiceStatus } from './useStripeInvoices';

interface Props {
  invoice: Invoice | null;
  onClose: () => void;
  onUpdateStatus: (id: string, status: InvoiceStatus) => void;
}

function isOverdue(inv: Invoice) {
  return inv.status === 'sent' && inv.due_date && new Date(inv.due_date) < new Date();
}

export default function InvoiceDetail({ invoice, onClose, onUpdateStatus }: Props) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const isOpen = !!invoice;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md shadow-2xl transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          background: 'var(--dash-card)',
          borderLeft: '1px solid var(--dash-border)',
        }}
      >
        <div className="flex flex-col h-full">
          <div
            className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '1px solid var(--dash-border)' }}
          >
            <div className="flex items-center gap-3">
              <h2
                className="font-sans text-base font-medium"
                style={{ color: 'var(--dash-text-primary)' }}
              >
                {invoice?.number || 'Invoice'}
              </h2>
              {invoice && <InvoiceStatusBadge status={invoice.status} dueDate={invoice.due_date} />}
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded transition-colors"
              style={{
                color: hoveredBtn === 'close' ? 'var(--dash-text-tertiary)' : 'var(--dash-text-dim)',
                background: hoveredBtn === 'close' ? 'var(--dash-hover-bg)' : 'transparent',
              }}
              onMouseEnter={() => setHoveredBtn('close')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <X size={18} />
            </button>
          </div>

          {invoice && (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <section>
                <div
                  className="font-mono text-[10px] uppercase tracking-widest mb-2"
                  style={{ color: 'var(--dash-text-dim)' }}
                >
                  Recipient
                </div>
                <div
                  className="font-sans text-sm"
                  style={{ color: 'var(--dash-text-secondary)' }}
                >
                  {invoice.recipient}
                </div>
              </section>

              <section
                className="pt-4"
                style={{ borderTop: '1px solid var(--dash-border)' }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="font-mono text-[10px] uppercase tracking-widest"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Amount
                  </span>
                  <span
                    className="font-mono text-lg"
                    style={{ color: 'var(--dash-text-primary)' }}
                  >
                    ${Number(invoice.amount).toFixed(2)}
                  </span>
                </div>
              </section>

              {invoice.description && (
                <section>
                  <div
                    className="font-mono text-[10px] uppercase tracking-widest mb-2"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Description
                  </div>
                  <p
                    className="font-sans text-sm leading-relaxed"
                    style={{ color: 'var(--dash-text-muted)' }}
                  >
                    {invoice.description}
                  </p>
                </section>
              )}

              {invoice.notes && (
                <section>
                  <div
                    className="font-mono text-[10px] uppercase tracking-widest mb-2"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Notes (internal)
                  </div>
                  <p
                    className="font-sans text-sm leading-relaxed"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    {invoice.notes}
                  </p>
                </section>
              )}

              <section className="space-y-2">
                <div
                  className="font-mono text-[10px] uppercase tracking-widest mb-2"
                  style={{ color: 'var(--dash-text-dim)' }}
                >
                  Details
                </div>
                <div className="flex justify-between">
                  <span
                    className="font-sans text-xs"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Invoice #
                  </span>
                  <span
                    className="font-mono text-xs"
                    style={{ color: 'var(--dash-text-tertiary)' }}
                  >
                    {invoice.number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className="font-sans text-xs"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Created
                  </span>
                  <span
                    className="font-mono text-xs"
                    style={{ color: 'var(--dash-text-tertiary)' }}
                  >
                    {timeAgo(invoice.created_at)}
                  </span>
                </div>
                {invoice.due_date && (
                  <div className="flex justify-between">
                    <span
                      className="font-sans text-xs"
                      style={{ color: 'var(--dash-text-dim)' }}
                    >
                      Due Date
                    </span>
                    <span className={`font-mono text-xs ${isOverdue(invoice) ? 'text-red-400' : ''}`}
                      style={isOverdue(invoice) ? undefined : { color: 'var(--dash-text-tertiary)' }}
                    >
                      {invoice.due_date}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span
                    className="font-sans text-xs"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Status
                  </span>
                  <InvoiceStatusBadge status={invoice.status} dueDate={invoice.due_date} />
                </div>
              </section>
            </div>
          )}

          {invoice && (
            <div
              className="px-6 py-4 space-y-2"
              style={{ borderTop: '1px solid var(--dash-border)' }}
            >
              {invoice.status === 'draft' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => { onUpdateStatus(invoice.id, 'sent'); onClose(); }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-white text-zinc-900 font-sans text-xs font-medium hover:bg-zinc-200 transition-colors"
                  >
                    <Send size={12} /> Send Invoice
                  </button>
                  <button
                    onClick={() => {
                      if (!confirmCancel) { setConfirmCancel(true); return; }
                      onUpdateStatus(invoice.id, 'cancelled');
                      setConfirmCancel(false);
                      onClose();
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded border font-sans text-xs transition-colors ${
                      confirmCancel
                        ? 'border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                        : ''
                    }`}
                    style={confirmCancel ? undefined : {
                      borderColor: 'var(--dash-border)',
                      color: hoveredBtn === 'cancel-draft' ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-dim)',
                    }}
                    onMouseEnter={() => setHoveredBtn('cancel-draft')}
                    onMouseLeave={() => setHoveredBtn(null)}
                  >
                    <XCircle size={12} /> {confirmCancel ? 'Confirm Cancel' : 'Cancel'}
                  </button>
                </div>
              )}
              {(invoice.status === 'sent' || isOverdue(invoice)) && (
                <div className="flex gap-2">
                  <button
                    onClick={() => { onUpdateStatus(invoice.id, 'paid'); onClose(); }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-emerald-600 text-white font-sans text-xs font-medium hover:bg-emerald-500 transition-colors"
                  >
                    <CheckCircle size={12} /> Mark Paid
                  </button>
                  <button
                    onClick={() => {
                      if (!confirmCancel) { setConfirmCancel(true); return; }
                      onUpdateStatus(invoice.id, 'cancelled');
                      setConfirmCancel(false);
                      onClose();
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded border font-sans text-xs transition-colors ${
                      confirmCancel
                        ? 'border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                        : ''
                    }`}
                    style={confirmCancel ? undefined : {
                      borderColor: 'var(--dash-border)',
                      color: hoveredBtn === 'cancel-sent' ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-dim)',
                    }}
                    onMouseEnter={() => setHoveredBtn('cancel-sent')}
                    onMouseLeave={() => setHoveredBtn(null)}
                  >
                    <XCircle size={12} /> {confirmCancel ? 'Confirm Cancel' : 'Cancel'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
